import { useState } from 'react';
import { Button, Dialog, InlineError, Select, Skeleton } from '../../components/ui';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import { useCgItems, useCgOrders } from './hooks';
import { ProductionPoDocument } from './ProductionPoDocument';
import { buildProductionPo } from './productionPo';
import type { ProductionPoLink } from './productionPo';

/**
 * Turning an acknowledged design handoff into a Production PO.
 *
 * The handoff itself carries almost nothing a factory can build from — a
 * title, some notes, tags and a few links. Everything a PO needs (client,
 * quantities, SKUs, the ship-to block, the target date) already lives on the
 * CG order, so this asks which order the handoff is for rather than inventing
 * a second place production quantities are kept.
 *
 * The order list is behind the CG Orders module permission, and the query does
 * not fire when that permission is refused — a picker that lists every client
 * order to somebody who is not allowed to see CG Orders would leak the whole
 * book through a dropdown.
 */

function todayIso(now: Date): string {
  return now.toISOString().slice(0, 10);
}

export function GenerateProductionPo({
  manufacturer,
  attn,
  address,
  designLinks,
  linkedOrderId,
  onClose,
  onLink,
  isLinking,
}: {
  manufacturer: string;
  /** The factory's named contact and address, so the printed sheet and the
      frozen one carry the same letterhead. */
  attn: string;
  address: string;
  designLinks: ProductionPoLink[];
  /** The order already stamped on this handoff, if it has one. */
  linkedOrderId: string | null;
  onClose: () => void;
  /** Stamps handoff_po_id. Absent when the caller does not own the handoff. */
  onLink?: (orderId: string) => void;
  isLinking?: boolean;
}) {
  const permissions = useMyPermissions();
  const canReachCg = canEditModule(permissions.data, 'cg_orders');
  const orders = useCgOrders();
  const items = useCgItems();
  const [orderId, setOrderId] = useState(linkedOrderId ?? '');
  const [error, setError] = useState('');

  const orderRows = canReachCg ? (orders.data ?? []) : [];
  const chosen = orderRows.find((order) => order.id === orderId) ?? null;
  const chosenItems = (items.data ?? []).filter((item) => item.order_id === orderId);

  function print() {
    document.body.classList.add('po-printing');
    window.addEventListener('afterprint', () => document.body.classList.remove('po-printing'), {
      once: true,
    });
    window.print();
  }

  return (
    <div className="po-print-root">
      <Dialog
        open
        wide
        title="Production PO"
        onClose={onClose}
        footer={
          <div className="po-no-print" style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            {onLink === undefined || chosen === null ? null : (
              <Button
                variant="secondary"
                disabled={isLinking === true || linkedOrderId === chosen.id}
                onClick={() => {
                  setError('');
                  onLink(chosen.id);
                }}
              >
                {linkedOrderId === chosen.id ? 'Linked to this handoff' : 'Link to this handoff'}
              </Button>
            )}
            <Button onClick={print} disabled={chosen === null}>
              Print / PDF
            </Button>
          </div>
        }
      >
        {error === '' ? null : <InlineError>{error}</InlineError>}

        {!canReachCg ? (
          <InlineError>
            Generating a PO needs the CG Orders module. Ask an admin for access.
          </InlineError>
        ) : orders.isPending || items.isPending ? (
          <Skeleton lines={3} label="Loading orders" />
        ) : (
          <>
            <div className="po-no-print">
              <Select
                label="Which CG order is this handoff for?"
                value={orderId}
                placeholder="Choose an order…"
                options={orderRows.map((order) => ({
                  value: order.id,
                  label: `${order.order_no} — ${order.client}`,
                }))}
                onChange={(event) => setOrderId(event.target.value)}
              />
            </div>

            {chosen === null ? (
              <p style={{ color: 'var(--text-muted)' }}>
                Pick an order to see the PO. Nothing is sent to the supplier from here — this is the
                document you print or save.
              </p>
            ) : (
              <ProductionPoDocument
                po={buildProductionPo(chosen, chosenItems, {
                  manufacturer,
                  attn,
                  address,
                  issuedOn: todayIso(new Date()),
                  designLinks,
                })}
              />
            )}
          </>
        )}
      </Dialog>
    </div>
  );
}
